import { KYCFileType, KycWorkflowSettingResponseDto } from '@dealstack/shared/lib/generated';

import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { KycInstructionBar } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/KycInstructions/KycInstructionBar';
import { Flex, Text } from '@mantine/core';
import styled from 'styled-components';
import { ProofFileBox } from '@dealstack/frontend/investor/pages/Investment/common/ProofFileBox/ProofFileBox';
import { useInvestmentManagePersonalIDsController } from './useInvestmentManagePersonalIDsController';
import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';
import { isKycRejected } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { isPersonalProofCertificationRequired } from '@dealstack/shared/lib/common/business/kycCertification';
import { CertifiableProofUpload } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/CertifiableProofUpload';
import { ProofCertificationStatus } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/ProofCertificationStatus';
import { isCertificationRequiredFor } from '@dealstack/frontend/investor/pages/Investment/common/CertifiableProof/CertifiableProof.utils';

interface Props {
	className?: string;
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	kycWorkflowId: string;
	kycSetting?: KycWorkflowSettingResponseDto;
}

export const InvestmentManagePersonalIDs = ({
	setIsContinueDisabled,
	kycSetting,
	kycWorkflowId,
}: Props) => {
	const tInvestor = useTranslationInvestor();

	const isCertifiedCopiesRequested = isPersonalProofCertificationRequired(kycSetting);

	const {
		proofOfIdentityDetails,
		proofOfAddressDetails,
		onDropProofOfIdentity,
		onDeleteProofOfIdentity,
		onDropProofOfAddress,
		onDeleteProofOfAddress,
		isLoading,
		refetchAll,
		uploadProofOfAddressRequest,
		uploadProofOfIdentityRequest,
	} = useInvestmentManagePersonalIDsController({ setIsContinueDisabled, kycWorkflowId });

	return (
		<Flex maw="40rem" direction="column" gap="2rem">
			{isLoading && <InlineLoader />}
			<ProofWrapper>
				<Text c="dark.0">{tInvestor('investment.join.kyc.proof_id')}</Text>
				{!!proofOfIdentityDetails && (
					<ProofFileBox
						fileName={proofOfIdentityDetails.fileName}
						fileId={proofOfIdentityDetails.fileId}
						onDelete={onDeleteProofOfIdentity}
						rejectionComment={proofOfIdentityDetails.rejectionComment}
						isRejected={isKycRejected(proofOfIdentityDetails.status)}
						date={proofOfIdentityDetails.createdAt}
						certificationSlot={
							<ProofCertificationStatus
								kycFile={proofOfIdentityDetails}
								certificationRequired={isCertificationRequiredFor(
									kycSetting,
									KYCFileType.PROOF_OF_IDENTITY
								)}
								isRejected={isKycRejected(proofOfIdentityDetails.status)}
								onChanged={refetchAll}
							/>
						}
					/>
				)}
				{!proofOfIdentityDetails && (
					<CertifiableProofUpload
						certificationRequired={isCertificationRequiredFor(
							kycSetting,
							KYCFileType.PROOF_OF_IDENTITY
						)}
						onDropFile={onDropProofOfIdentity}
						loading={uploadProofOfIdentityRequest.isPending}
						dataTestId={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_ID_FILE_INPUT}
					/>
				)}
			</ProofWrapper>

			<ProofWrapper>
				<Text c="dark.0">{tInvestor('investment.join.kyc.proof_address')}</Text>
				{!!proofOfAddressDetails && (
					<ProofFileBox
						fileName={proofOfAddressDetails.fileName}
						fileId={proofOfAddressDetails.fileId}
						onDelete={onDeleteProofOfAddress}
						rejectionComment={proofOfAddressDetails.rejectionComment}
						isRejected={isKycRejected(proofOfAddressDetails.status)}
						date={proofOfAddressDetails.createdAt}
						certificationSlot={
							<ProofCertificationStatus
								kycFile={proofOfAddressDetails}
								certificationRequired={isCertificationRequiredFor(
									kycSetting,
									KYCFileType.PROOF_OF_ADDRESS
								)}
								isRejected={isKycRejected(proofOfAddressDetails.status)}
								onChanged={refetchAll}
							/>
						}
					/>
				)}
				{!proofOfAddressDetails && (
					<CertifiableProofUpload
						certificationRequired={isCertificationRequiredFor(
							kycSetting,
							KYCFileType.PROOF_OF_ADDRESS
						)}
						onDropFile={onDropProofOfAddress}
						loading={uploadProofOfAddressRequest.isPending}
						dataTestId={INVESTOR_TEST_ELEMENT_ID.INVESTOR_KYC_ADDRESS_FILE_INPUT}
					/>
				)}
			</ProofWrapper>

			<KycInstructionBar isCertifiedCopiesOfDocumentsRequested={isCertifiedCopiesRequested} />
		</Flex>
	);
};

const ProofWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	padding: 0.5rem;
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[4]};
	border-radius: 4px;
`;
